/**
 * @lfoc/domain/permissions - which Console screens and actions a person is
 * OFFERED, from the Core claims /api/me returns (IP-46, IP-80).
 *
 * One layer. The LIV user-type and LIV organisation-claim layers are gone
 * with LIV sign-in (IP-72): a person's permissions are the union of their
 * Core roles' claims, and nothing else. The authorizer computes that union on
 * every request, and every route checks its own claim (the catalogue for a
 * write, view() for a read) - so this module is a courtesy to the person, not
 * a control: it decides what the page shows and which buttons are enabled,
 * and the server refuses anything else whatever the page did.
 *
 * Browser-safe: no Node module.
 */

/**
 * Screens, the claims that make each visible (any one), and per action the
 * claims that allow it (any one). Keys match the Console's module and section
 * keys; the claim codes are authority/claims.js's. An action key that stands
 * for a catalogue action carries exactly that action's claims
 * (authority/catalogue.js): one key never serves two server claims, or the
 * page offers a button the server refuses.
 */
export const SCREEN_RULES = Object.freeze({
  uploads: {
    view: ['core.uploads.view'],
    // IP-103: initial - the INITIAL load kind is offered at all (upload, and
    // accept alongside accept's own claim); the registry judges the rest.
    actions: { upload: ['core.intake.upload'], accept: ['core.intake.accept'], reject: ['core.intake.accept'], initial: ['core.intake.initial'] },
  },
  unitPrices: {
    view: ['core.prices.view'],
    actions: {
      approve: ['core.price.approve'],
      reject: ['core.price.approve'],
      override: ['core.price.override'],
    },
  },
  approval: {
    view: ['core.dealing.view'],
    actions: {
      review: ['core.order.review'],
      // ORDER_APPROVE, and IP-85, IP-86: the checker's other two answers to a reviewed order - core.order.approve's alone.
      approve: ['core.order.approve'],
      sendBack: ['core.order.approve'],
      reject: ['core.order.approve'],
      // MATCH_APPROVE / MATCH_REJECT: a payment match's checker.
      approveMatch: ['core.cash.approve'],
      rejectMatch: ['core.cash.approve'],
      match: ['core.cash.claim'],
      clear: ['core.cash.claim'],
      // IP-112: a cheque's return (LINE_RETURN), the refund of money nobody
      // could match (RECEIPT_REFUND), and a note on a line (LINE_NOTE).
      returnLine: ['core.cash.claim'],
      // IP-138: an automatic match undone (LINE_UNRECONCILE) - the checker's, as MATCH_REJECT.
      unreconcile: ['core.cash.approve'],
      // IP-157: a left-over line cleared by hand (LINE_MANUAL_CLEAR) - the checker's.
      manualClear: ['core.cash.approve'],
      refund: ['core.payout.manage'],
      note: ['core.cash.claim', 'core.cash.approve'],
      manage: ['core.order.manage'],
      closeBook: ['core.book.close'],
      generateDocument: ['core.payout.manage'],
    },
  },
  registry: { view: ['core.registry.view'], actions: { extract: ['core.registry.extract'] } },
  reports: { view: ['core.reports.view'], actions: { run: ['core.reports.view'] } },
  // Reference data: whoever works with prices, dealing or the register may read
  // it. IP-101, IP-102: a fund's fees change here - proposed by one person,
  // approved or rejected by another; the fee claims read it too.
  fundConfig: {
    view: ['core.prices.view', 'core.dealing.view', 'core.registry.view', 'core.fees.propose', 'core.fees.approve'],
    actions: { propose: ['core.fees.propose'], approve: ['core.fees.approve'], reject: ['core.fees.approve'] },
  },
  calendar: { view: ['core.prices.view', 'core.dealing.view', 'core.registry.view'], actions: {} },
  // IP-134, IP-135: the topbar's business-day chip and its drawer (GET
  // books/days reads under any view claim - authority/claims.js VIEW_CLAIMS,
  // written out here, since this module imports nothing); advance is
  // DAY_ADVANCE's own claim, the book close's. In the Console this is
  // businessDay, never stage: principal.stage is the deployment environment.
  businessDay: {
    view: ['core.uploads.view', 'core.prices.view', 'core.dealing.view', 'core.registry.view', 'core.reports.view', 'core.clients.view'],
    // IP-195: runDayEnd - the Run day end button (DAY_END_RUN), the same claim as advance.
    actions: { advance: ['core.book.close'], runDayEnd: ['core.book.close'] },
  },
  // The day-end analytics feeds (IP-142 to IP-156): their own module since the
  // owner moved them out of the business-day drawer (9 Oct 2026). GET
  // books/feeds reads under any view claim (VIEW_CLAIMS, as businessDay's);
  // a re-send and the WM waiver are core.feeds.send's.
  analyticsFeeds: {
    view: ['core.uploads.view', 'core.prices.view', 'core.dealing.view', 'core.registry.view', 'core.reports.view', 'core.clients.view'],
    actions: { resend: ['core.feeds.send'], waiveWm: ['core.feeds.send'] },
  },
  userManagement: {
    view: ['core.access.admin'],
    actions: {
      invite: ['core.access.admin'],
      resendInvite: ['core.access.admin'],
      changeRoles: ['core.access.admin'],
      resetMfa: ['core.access.admin'],
      suspend: ['core.access.admin'],
      reinstate: ['core.access.admin'],
      editRoles: ['core.access.admin'],
      // NOTIFICATION_CONFIG_SAVE (docs/notifications/implementation-guide.md 8.1).
      editNotifications: ['core.access.admin'],
    },
  },
});

export const SCREENS = Object.freeze(Object.keys(SCREEN_RULES));

/**
 * What a person may see and do.
 *
 * @param {{ claims: string[] }} person
 * @returns {Record<string, { actions: string[] }>} only the visible screens
 */
export function permissionsFor({ claims } = {}) {
  const held = new Set(Array.isArray(claims) ? claims : []);
  const anyOf = (codes) => codes.some((code) => held.has(code));
  const out = {};
  for (const [screen, rule] of Object.entries(SCREEN_RULES)) {
    if (!anyOf(rule.view)) continue;
    out[screen] = { actions: Object.entries(rule.actions).filter(([, codes]) => anyOf(codes)).map(([action]) => action) };
  }
  return out;
}

/** Whether the person may take `action` on `screen`. */
export function mayAct(person, screen, action) {
  return permissionsFor(person)[screen]?.actions.includes(action) ?? false;
}
